/**
 * Publishes a pre-recorded MP4 in place of the camera/microphone during
 * delivery video calls.
 *
 * Driven entirely by the backend: each pharmacy team user carries a
 * `video_stream_enabled` flag and an uploaded clip, and the delivery call
 * accept payload returns both as `video_stream_enabled` / `video_stream_url`.
 * There is no client-side configuration — turning it on or off is done from the
 * team user screen.
 *
 * The clip is served from the backend's public storage. That is a different
 * origin than the portal in most environments, so the fetch below needs
 * `Access-Control-Allow-Origin`; when it fails the element loads the URL
 * directly instead (uncached, but still works).
 */

const preloadPromises = new Map();
const cachedObjectUrls = new Map();

export const isDeliveryCallStreamEnabled = (callData) =>
  Boolean(callData?.video_stream_enabled) && Boolean(callData?.video_stream_url);

/**
 * Pulls the clip into memory ahead of the first call so playback starts with no
 * network round trip. Resolves to a blob URL, or null when the fetch fails —
 * callers treat null as "load straight from the source URL instead".
 */
export const preloadDeliveryCallStream = (streamUrl) => {
  if (!streamUrl) return Promise.resolve(null);
  if (cachedObjectUrls.has(streamUrl)) {
    return Promise.resolve(cachedObjectUrls.get(streamUrl));
  }
  if (preloadPromises.has(streamUrl)) {
    return preloadPromises.get(streamUrl);
  }

  const promise = fetch(streamUrl, { cache: 'force-cache' })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Delivery call stream responded ${response.status}.`);
      }
      return response.blob();
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      cachedObjectUrls.set(streamUrl, objectUrl);
      return objectUrl;
    })
    .catch((error) => {
      console.error('Unable to preload the delivery call stream:', error);
      // Drop the entry so a later call can retry rather than being stuck here.
      preloadPromises.delete(streamUrl);
      return null;
    });

  preloadPromises.set(streamUrl, promise);
  return promise;
};

const waitForPlayableData = (videoElement) =>
  new Promise((resolve, reject) => {
    if (videoElement.readyState >= 2) {
      resolve();
      return;
    }

    const cleanup = () => {
      videoElement.removeEventListener('loadeddata', handleLoaded);
      videoElement.removeEventListener('error', handleError);
    };

    function handleLoaded() {
      cleanup();
      resolve();
    }

    function handleError() {
      cleanup();
      reject(videoElement.error || new Error('Delivery call stream failed to load.'));
    }

    videoElement.addEventListener('loadeddata', handleLoaded);
    videoElement.addEventListener('error', handleError);
  });

const captureElementStream = (videoElement) => {
  if (typeof videoElement.captureStream === 'function') {
    return videoElement.captureStream();
  }
  if (typeof videoElement.mozCaptureStream === 'function') {
    return videoElement.mozCaptureStream();
  }
  throw new Error('This browser cannot capture a stream from a video element.');
};

/**
 * Starts the clip and hands back its tracks for Vonage to publish.
 * Returns { videoTrack, audioTrack, stop }.
 */
export const createDeliveryCallStreamSource = async (streamUrl) => {
  if (!streamUrl) {
    throw new Error('No delivery call stream URL was provided.');
  }

  const sourceUrl = (await preloadDeliveryCallStream(streamUrl)) || streamUrl;
  const isCrossOrigin = /^https?:\/\//i.test(sourceUrl)
    && typeof window !== 'undefined'
    && !sourceUrl.startsWith(window.location.origin);

  const videoElement = document.createElement('video');
  videoElement.src = sourceUrl;
  videoElement.loop = true;
  videoElement.playsInline = true;
  videoElement.preload = 'auto';
  // Muted keeps the clip silent on the pharmacist's own machine and exempts it
  // from autoplay blocking. captureStream() still carries the audio to the peer.
  videoElement.muted = true;
  if (isCrossOrigin && !sourceUrl.startsWith('blob:')) {
    videoElement.crossOrigin = 'anonymous';
  }
  // The element MUST stay rendered with a real size. Browsers throttle frame
  // production for hidden / `display:none` / zero-size video, which would freeze
  // the outgoing picture. Occlusion by another element does NOT trigger that
  // throttle, so it is parked in the self-view corner *behind* Vonage's own
  // preview of the published track (no visible duplicate).
  videoElement.setAttribute('aria-hidden', 'true');
  videoElement.style.cssText =
    'position:fixed;bottom:20px;right:20px;width:160px;height:208px;object-fit:cover;z-index:0;pointer-events:none;';

  let stream = null;
  let frameKeepAliveHandle = null;
  let resumeOnVisibility = null;

  const stop = () => {
    if (frameKeepAliveHandle && typeof videoElement.cancelVideoFrameCallback === 'function') {
      try {
        videoElement.cancelVideoFrameCallback(frameKeepAliveHandle);
      } catch (error) {}
    }
    frameKeepAliveHandle = null;
    if (resumeOnVisibility) {
      document.removeEventListener('visibilitychange', resumeOnVisibility);
      window.removeEventListener('focus', resumeOnVisibility);
      resumeOnVisibility = null;
    }
    try {
      stream?.getTracks().forEach((track) => track.stop());
    } catch (error) {}
    try {
      videoElement.pause();
      videoElement.removeAttribute('src');
      videoElement.load();
    } catch (error) {}
    videoElement.remove();
  };

  try {
    document.body.appendChild(videoElement);
    await waitForPlayableData(videoElement);
    await videoElement.play();

    // A backgrounded tab still throttles playback; nudge it back whenever the
    // pharmacist returns to the call so the outgoing feed un-freezes.
    resumeOnVisibility = () => {
      if (!document.hidden) {
        videoElement.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', resumeOnVisibility);
    window.addEventListener('focus', resumeOnVisibility);

    // Keep-alive: touching each presented frame discourages aggressive
    // throttling on browsers that expose requestVideoFrameCallback.
    if (typeof videoElement.requestVideoFrameCallback === 'function') {
      const pump = () => {
        frameKeepAliveHandle = videoElement.requestVideoFrameCallback(pump);
      };
      pump();
    }

    stream = captureElementStream(videoElement);
    const videoTrack = stream.getVideoTracks()[0] || null;
    const audioTrack = stream.getAudioTracks()[0] || null;

    if (!videoTrack) {
      throw new Error('Delivery call stream produced no video track.');
    }

    return { videoTrack, audioTrack, stop };
  } catch (error) {
    stop();
    throw error;
  }
};
